import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Bed, Save, Loader2, Trash2, Plus, Sparkles, Upload, ArrowLeft } from 'lucide-react';
import { useTabs } from '../context/TabsContext';

const RoomForm = ({ tabData }) => {
  const { closeTab } = useTabs();
  const room = tabData?.room || null;
  const isEdit = !!room;
  const onComplete = tabData?.onComplete;

  const [roomTypes, setRoomTypes] = useState([]);
  const [amenities, setAmenities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [formData, setFormData] = useState({
    room_number: room?.room_number || '',
    room_type_id: room?.room_type_id || '',
    block_name: room?.block_name || '',
    floor: room?.floor?.toString() || '',
    capacity: room?.capacity?.toString() || '2',
    smoking_allowed: room?.smoking_allowed || false,
    amenity_ids: room?.amenity_ids || [],
    photos: room?.photos || [],
    // Room size
    room_size: room?.room_size?.toString() || '',
    room_size_unit: room?.room_size_unit || 'sqft',
    // Guest capacity overrides
    override_adults: room?.override_adults?.toString() || '',
    override_children: room?.override_children?.toString() || '',
    max_adults: room?.max_adults?.toString() || '',
    max_children: room?.max_children?.toString() || ''
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        const [rtRes, amRes] = await Promise.all([
          api.get('/room-types'),
          api.get('/amenities')
        ]);
        setRoomTypes(rtRes.data.filter(rt => rt.is_active !== false));
        setAmenities(amRes.data.filter(a => a.is_active !== false));
      } catch (err) {
        toast.error('Failed to load data');
      }
    };
    loadData();
  }, []);

  const toggleAmenity = (amenityId) => {
    setFormData(prev => ({
      ...prev,
      amenity_ids: prev.amenity_ids.includes(amenityId)
        ? prev.amenity_ids.filter(id => id !== amenityId)
        : [...prev.amenity_ids, amenityId]
    }));
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingPhoto(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        const params = new URLSearchParams({ photo_type: 'room', entity_id: room?.id || 'new' });
        const res = await api.post(`/upload/portal-photo?${params.toString()}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setFormData(prev => ({
          ...prev,
          photos: [...prev.photos, res.data.photo_url]
        }));
      }
      toast.success(`${files.length} photo(s) uploaded`);
    } catch (err) {
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  };

  const removePhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!formData.room_type_id || !formData.room_number) {
      toast.error('Please fill required fields');
      return;
    }

    const roomType = roomTypes.find(rt => rt.id === formData.room_type_id);
    const data = {
      room_number: formData.room_number,
      room_type_id: formData.room_type_id,
      room_type: roomType?.name || '',
      block_name: formData.block_name || null,
      price_per_night: roomType?.base_price || 0,
      floor: parseInt(formData.floor) || 1,
      capacity: parseInt(formData.capacity) || 2,
      smoking_allowed: formData.smoking_allowed,
      amenity_ids: formData.amenity_ids,
      photos: formData.photos,
      bed_type_ids: [],
      // Room size
      room_size: formData.room_size ? parseFloat(formData.room_size) : null,
      room_size_unit: formData.room_size_unit || 'sqft',
      // Guest capacity overrides
      override_adults: formData.override_adults ? parseInt(formData.override_adults) : null,
      override_children: formData.override_children ? parseInt(formData.override_children) : null,
      max_adults: formData.max_adults ? parseInt(formData.max_adults) : null,
      max_children: formData.max_children ? parseInt(formData.max_children) : null
    };

    setLoading(true);
    try {
      if (isEdit) {
        await api.put(`/rooms/${room.id}`, data);
        toast.success('Room updated');
      } else {
        await api.post('/rooms', data);
        toast.success('Room created');
      }
      if (onComplete) onComplete();
      closeTab();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save room');
    } finally {
      setLoading(false);
    }
  }, [formData, roomTypes, isEdit, room, onComplete, closeTab]);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={closeTab} data-testid="room-form-back-btn">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-800" data-testid="room-form-title">
            {isEdit ? `Edit Room ${room.room_number}` : 'Add New Room'}
          </h1>
          <p className="text-sm text-gray-500">{isEdit ? 'Update room details' : 'Create a new room'}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Basic Details */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Bed className="h-4 w-4 text-orange-500" />
                  Room Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Room Number *</Label>
                    <Input
                      data-testid="room-number-input"
                      value={formData.room_number}
                      onChange={(e) => setFormData(prev => ({ ...prev, room_number: e.target.value }))}
                      placeholder="101"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Room Type *</Label>
                    <Select
                      value={formData.room_type_id}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, room_type_id: value }))}
                    >
                      <SelectTrigger data-testid="room-type-select">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {roomTypes.map((rt) => (
                          <SelectItem key={rt.id} value={rt.id}>
                            {rt.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Block / Wing</Label>
                    <Input
                      data-testid="room-block-input"
                      value={formData.block_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, block_name: e.target.value }))}
                      placeholder="Block A"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Floor *</Label>
                    <Input
                      data-testid="room-floor-input"
                      type="number"
                      value={formData.floor}
                      onChange={(e) => setFormData(prev => ({ ...prev, floor: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Capacity *</Label>
                    <Input
                      data-testid="room-capacity-input"
                      type="number"
                      min="1"
                      value={formData.capacity}
                      onChange={(e) => setFormData(prev => ({ ...prev, capacity: e.target.value }))}
                      required
                    />
                  </div>
                </div>
                
                {/* Room Size */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Room Size</Label>
                    <div className="flex gap-2">
                      <Input
                        data-testid="room-size-input"
                        type="number"
                        min="0"
                        value={formData.room_size}
                        onChange={(e) => setFormData(prev => ({ ...prev, room_size: e.target.value }))}
                        placeholder="500"
                        className="flex-1"
                      />
                      <Select
                        value={formData.room_size_unit}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, room_size_unit: value }))}
                      >
                        <SelectTrigger className="w-24">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="sqft">sq ft</SelectItem>
                          <SelectItem value="sqm">sq m</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
                
                {/* Guest Capacity Overrides */}
                <div className="border rounded-lg p-4 space-y-3 bg-gray-50">
                  <Label className="font-medium text-sm">Guest Capacity (Override Hotel Defaults)</Label>
                  <div className="grid grid-cols-4 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-gray-500">Default Adults</Label>
                      <Input
                        type="number"
                        min="1"
                        value={formData.override_adults}
                        onChange={(e) => setFormData(prev => ({ ...prev, override_adults: e.target.value }))}
                        placeholder="2"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-gray-500">Default Children</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.override_children}
                        onChange={(e) => setFormData(prev => ({ ...prev, override_children: e.target.value }))}
                        placeholder="0"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-gray-500">Max Adults</Label>
                      <Input
                        type="number"
                        min="1"
                        value={formData.max_adults}
                        onChange={(e) => setFormData(prev => ({ ...prev, max_adults: e.target.value }))}
                        placeholder="3"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-gray-500">Max Children</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.max_children}
                        onChange={(e) => setFormData(prev => ({ ...prev, max_children: e.target.value }))}
                        placeholder="2"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-400">Leave empty to use room type defaults set by Company Admin</p>
                </div>
                
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="smoking"
                    data-testid="room-smoking-checkbox"
                    checked={formData.smoking_allowed}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, smoking_allowed: checked }))}
                  />
                  <Label htmlFor="smoking" className="text-sm cursor-pointer">Smoking Allowed</Label>
                </div>
              </CardContent>
            </Card>

            {/* Photos Card */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Upload className="h-4 w-4 text-orange-500" />
                  Room Photos
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid="room-form-photo-upload-btn"
                    onClick={() => document.getElementById('room-form-photo-input').click()}
                    disabled={uploadingPhoto}
                    className="gap-1"
                  >
                    {uploadingPhoto ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                    {uploadingPhoto ? 'Uploading...' : 'Upload Photos'}
                  </Button>
                  <input
                    id="room-form-photo-input"
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    className="hidden"
                    onChange={handlePhotoUpload}
                  />
                  <span className="text-xs text-gray-400 self-center">JPEG, PNG, WebP — max 10MB</span>
                </div>
                {formData.photos.length > 0 ? (
                  <div className="grid grid-cols-4 gap-3">
                    {formData.photos.map((photo, idx) => (
                      <div key={idx} className="relative group aspect-video rounded-lg overflow-hidden bg-gray-100" data-testid={`room-form-photo-${idx}`}>
                        <img
                          src={photo.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${photo}` : photo}
                          alt={`Room photo ${idx + 1}`}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.src = 'https://placehold.co/400x300?text=Error'; }}
                        />
                        <button
                          type="button"
                          className="absolute top-1 right-1 h-6 w-6 rounded bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => removePhoto(idx)}
                          data-testid={`remove-room-form-photo-${idx}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 bg-gray-50 rounded-lg border-2 border-dashed">
                    <Upload className="h-8 w-8 mx-auto text-gray-300 mb-1" />
                    <p className="text-sm text-gray-400">No photos yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Amenities */}
          <div className="space-y-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-orange-500" />
                  Amenities
                  <Badge variant="outline" className="ml-auto">{formData.amenity_ids.length} selected</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {amenities.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-4">No amenities available</p>
                ) : (
                  <div className="space-y-1.5 max-h-80 overflow-y-auto" data-testid="room-form-amenity-list">
                    {amenities.map((amenity) => {
                      const isSelected = formData.amenity_ids.includes(amenity.id);
                      return (
                        <button
                          type="button"
                          key={amenity.id}
                          data-testid={`room-form-amenity-${amenity.id}`}
                          onClick={() => toggleAmenity(amenity.id)}
                          className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors text-left ${
                            isSelected
                              ? 'border-orange-500 bg-orange-50 text-orange-700'
                              : 'border-gray-200 hover:border-gray-300 text-gray-600'
                          }`}
                        >
                          <div className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 ${
                            isSelected ? 'bg-orange-500 border-orange-500' : 'border-gray-300'
                          }`}>
                            {isSelected && <svg className="h-3 w-3 text-white" viewBox="0 0 12 12"><path d="M10 3L4.5 8.5 2 6" stroke="currentColor" strokeWidth="2" fill="none" /></svg>}
                          </div>
                          {amenity.name}
                        </button>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Save Button */}
            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white h-11"
              disabled={loading || !formData.room_type_id || !formData.room_number}
              data-testid="room-form-save-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              {isEdit ? 'Update Room' : 'Create Room'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default RoomForm;
